import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArticleAutocomplete } from '../components/ui/article-autocomplete';
import { Combobox } from '../components/ui/combobox';
import { 
  ArrowLeft, Save, Wrench, FileText, Package, DollarSign, 
  Upload, Video, CheckCircle, Plus, Trash2, Clock, User,
  AlertTriangle, Activity, ClipboardList, FileDown
} from 'lucide-react';

const RevisionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [symptoms, setSymptoms] = useState([]);
  const [causes, setCauses] = useState([]);
  const [interventionTypes, setInterventionTypes] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Form state
  const [diagnostic, setDiagnostic] = useState({
    symptom_id: '',
    cause_id: '',
    intervention_type_id: '',
    diagnostic_notes: ''
  });
  const [tasksPlanned, setTasksPlanned] = useState([]);
  const [tasksExecuted, setTasksExecuted] = useState([]);
  const [consumablesPlanned, setConsumablesPlanned] = useState([]);
  const [consumablesUsed, setConsumablesUsed] = useState([]);
  const [costs, setCosts] = useState({
    labor_hours_planned: 0,
    labor_hours_actual: 0,
    labor_hourly_rate: 50,
    labor_cost_planned: 0,
    labor_cost_actual: 0,
    materials_cost_planned: 0,
    materials_cost_actual: 0,
    total_cost_planned: 0,
    total_cost_actual: 0
  });
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [showAddConsumableDialog, setShowAddConsumableDialog] = useState(false);
  const [newTask, setNewTask] = useState({ description: '', is_planned: true });
  const [newConsumable, setNewConsumable] = useState({ article_id: '', quantity_planned: 0 });

  const fetchRevision = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/revisions/${id}`);
      const rev = response.data;
      setRevision(rev);
      
      // Initialize form state from revision
      setDiagnostic(rev.diagnostic || {
        symptom_id: '',
        cause_id: '',
        intervention_type_id: '',
        diagnostic_notes: ''
      });
      setTasksPlanned(rev.tasks_planned || rev.tasks || []);
      setTasksExecuted(rev.tasks_executed || []);
      setConsumablesPlanned(rev.consumables_planned || rev.consumables || []);
      setConsumablesUsed(rev.consumables_used || []);
      setCosts(rev.costs || {
        labor_hours_planned: rev.estimated_hours || 0,
        labor_hours_actual: rev.actual_hours || 0,
        labor_hourly_rate: 50,
        labor_cost_planned: (rev.estimated_hours || 0) * 50,
        labor_cost_actual: (rev.actual_hours || 0) * 50,
        materials_cost_planned: rev.estimated_cost || 0,
        materials_cost_actual: rev.actual_cost || 0,
        total_cost_planned: 0,
        total_cost_actual: 0
      });
      setDocuments(rev.documents || []);
      setVideos(rev.videos || []);
    } catch (error) {
      console.error('Error fetching revision:', error);
      toast.error('Eroare la incarcarea reviziei');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRevision();
    fetchReferenceData();
  }, [id, fetchRevision]);

  const fetchReferenceData = async () => {
    try {
      const [symptomsRes, causesRes, interventionTypesRes, consumablesRes, usersRes] = await Promise.all([
        api.get('/symptoms'),
        api.get('/causes'),
        api.get('/intervention-types'),
        api.get('/articles'),
        api.get('/users')
      ]);
      setSymptoms(symptomsRes.data);
      setCauses(causesRes.data);
      setInterventionTypes(interventionTypesRes.data);
      setConsumables(consumablesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const calculateCosts = useCallback(() => {
    const laborPlanned = costs.labor_hours_planned * costs.labor_hourly_rate;
    const laborActual = costs.labor_hours_actual * costs.labor_hourly_rate;
    const materialsPlanned = consumablesPlanned.reduce((sum, c) => sum + (c.quantity_planned || 0) * (c.unit_price || 0), 0);
    const materialsActual = consumablesUsed.reduce((sum, c) => sum + (c.quantity_used || 0) * (c.unit_price || 0), 0);
    
    setCosts(prev => ({
      ...prev,
      labor_cost_planned: laborPlanned,
      labor_cost_actual: laborActual,
      materials_cost_planned: materialsPlanned,
      materials_cost_actual: materialsActual,
      total_cost_planned: laborPlanned + materialsPlanned,
      total_cost_actual: laborActual + materialsActual
    }));
  }, [costs.labor_hours_planned, costs.labor_hours_actual, costs.labor_hourly_rate, consumablesPlanned, consumablesUsed]);

  useEffect(() => {
    calculateCosts();
  }, [calculateCosts]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        diagnostic,
        tasks_planned: tasksPlanned,
        tasks_executed: tasksExecuted,
        consumables_planned: consumablesPlanned,
        consumables_used: consumablesUsed,
        costs,
        actual_hours: costs.labor_hours_actual,
        actual_cost: costs.total_cost_actual
      };
      
      await api.put(`/revisions/${id}`, updateData);
      toast.success('Revizie salvata cu succes!');
      fetchRevision();
    } catch (error) {
      toast.error('Eroare la salvarea reviziei');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      const response = await api.get(`/revisions/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fisa_interventie_${revision?.equipment_code || 'REV'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF generat cu succes!');
    } catch (error) {
      toast.error('Eroare la generarea PDF');
    }
  };

  const handleAddTask = () => {
    if (!newTask.description) return;
    
    const task = {
      id: `task-${Date.now()}`,
      description: newTask.description,
      is_planned: newTask.is_planned,
      completed: false
    };
    
    if (newTask.is_planned) {
      setTasksPlanned([...tasksPlanned, task]);
    } else {
      setTasksExecuted([...tasksExecuted, task]);
    }
    
    setNewTask({ description: '', is_planned: true });
    setShowAddTaskDialog(false);
  };

  const handleToggleTaskComplete = (taskId, isPlanned) => {
    if (isPlanned) {
      setTasksPlanned(tasksPlanned.map(t => 
        t.id === taskId ? { ...t, completed: !t.completed, completed_at: !t.completed ? new Date().toISOString() : null } : t
      ));
    } else {
      setTasksExecuted(tasksExecuted.map(t => 
        t.id === taskId ? { ...t, completed: !t.completed, completed_at: !t.completed ? new Date().toISOString() : null } : t
      ));
    }
  };

  const handleDeleteTask = (taskId, isPlanned) => {
    if (isPlanned) {
      setTasksPlanned(tasksPlanned.filter(t => t.id !== taskId));
    } else {
      setTasksExecuted(tasksExecuted.filter(t => t.id !== taskId));
    }
  };

  const handleAddConsumable = () => {
    if (!newConsumable.article_id) return;
    
    const article = consumables.find(c => c.id === newConsumable.article_id);
    const consumable = {
      id: `cons-${Date.now()}`,
      article_id: newConsumable.article_id,
      article_name: article?.name || '',
      article_code: article?.code || '',
      unit: article?.unit || 'buc',
      quantity_planned: newConsumable.quantity_planned,
      quantity_used: 0,
      unit_price: article?.price || 0,
      total_price: 0,
      picked: false
    };
    
    setConsumablesPlanned([...consumablesPlanned, consumable]);
    setNewConsumable({ article_id: '', quantity_planned: 0 });
    setShowAddConsumableDialog(false);
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // In a real app, you would upload to a server here
    const fileData = {
      id: `${type}-${Date.now()}`,
      name: file.name,
      file_url: URL.createObjectURL(file),
      file_type: file.type,
      uploaded_at: new Date().toISOString()
    };
    
    if (type === 'document') {
      setDocuments([...documents, fileData]);
    } else {
      setVideos([...videos, fileData]);
    }
    
    toast.success(`${type === 'document' ? 'Document' : 'Video'} incarcat!`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      waiting_parts: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      planned: 'Planificat',
      in_progress: 'In Lucru',
      waiting_parts: 'Asteapta Piese',
      completed: 'Finalizat',
      cancelled: 'Anulat'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getRevisionTypeLabel = (type) => {
    const labels = {
      preventive: 'Preventiva',
      corrective: 'Corectiva',
      predictive: 'Predictiva'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  if (!revision) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Revizia nu a fost gasita</p>
        <Button variant="link" onClick={() => navigate(-1)}>Inapoi</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revision-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900">{revision.title}</h1>
              {getStatusBadge(revision.status)}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              <span>🔧 {revision.equipment_name}</span>
              <span>📍 {revision.location_name}</span>
              <span>📅 {new Date(revision.scheduled_date).toLocaleDateString('ro-RO')}</span>
              <Badge variant="outline">{getRevisionTypeLabel(revision.revision_type)}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleGeneratePdf} className="h-12" data-testid="generate-pdf-btn">
            <FileDown className="w-5 h-5 mr-2" />
            GENEREAZA PDF
          </Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary h-12" data-testid="save-revision-btn">
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'SE SALVEAZA...' : 'SALVEAZA'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-7 h-12">
          <TabsTrigger value="general" className="text-xs md:text-sm">General</TabsTrigger>
          <TabsTrigger value="diagnostic" className="text-xs md:text-sm">Diagnostic</TabsTrigger>
          <TabsTrigger value="tasks" className="text-xs md:text-sm">Sarcini</TabsTrigger>
          <TabsTrigger value="consumables" className="text-xs md:text-sm">Consumabile</TabsTrigger>
          <TabsTrigger value="documents" className="text-xs md:text-sm">Documente</TabsTrigger>
          <TabsTrigger value="videos" className="text-xs md:text-sm">Video</TabsTrigger>
          <TabsTrigger value="costs" className="text-xs md:text-sm">Costuri</TabsTrigger>
        </TabsList>

        {/* Tab: General */}
        <TabsContent value="general">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg">Informatii Generale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Titlu</Label>
                  <p className="font-medium">{revision.title}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Tip Revizie</Label>
                  <p className="font-medium">{getRevisionTypeLabel(revision.revision_type)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Echipament</Label>
                  <p className="font-medium">{revision.equipment_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Locatie</Label>
                  <p className="font-medium">{revision.location_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Data Planificata</Label>
                  <p className="font-medium">{new Date(revision.scheduled_date).toLocaleString('ro-RO')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Executor</Label>
                  <p className="font-medium">{revision.assigned_to_name || 'Neasignat'}</p>
                </div>
              </div>
              {revision.description && (
                <div>
                  <Label className="text-slate-500">Descriere</Label>
                  <p className="mt-1">{revision.description}</p>
                </div>
              )}
              {revision.procedure_name && (
                <div>
                  <Label className="text-slate-500">Procedura de Lucru</Label>
                  <p className="font-medium">{revision.procedure_name}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Diagnostic */}
        <TabsContent value="diagnostic">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Diagnostic Interventie
              </CardTitle>
              <CardDescription>Documentati simptomele, cauzele si tipul interventiei</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Simptom constatat</Label>
                  <Combobox
                    items={symptoms}
                    value={diagnostic.symptom_id}
                    onSelect={(id) => setDiagnostic({ ...diagnostic, symptom_id: id })}
                    placeholder="Selecteaza simptomul..."
                    searchPlaceholder="Cauta simptom..."
                    emptyText="Niciun simptom gasit."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cauza defectului</Label>
                  <Combobox
                    items={causes}
                    value={diagnostic.cause_id}
                    onSelect={(id) => setDiagnostic({ ...diagnostic, cause_id: id })}
                    placeholder="Selecteaza cauza..."
                    searchPlaceholder="Cauta cauza..."
                    emptyText="Niciun cauza gasita."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tip interventie</Label>
                  <Combobox
                    items={interventionTypes}
                    value={diagnostic.intervention_type_id}
                    onSelect={(id) => setDiagnostic({ ...diagnostic, intervention_type_id: id })}
                    placeholder="Selecteaza tipul..."
                    searchPlaceholder="Cauta tip interventie..."
                    emptyText="Niciun tip gasit."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note diagnostic</Label>
                <Textarea
                  value={diagnostic.diagnostic_notes}
                  onChange={(e) => setDiagnostic({ ...diagnostic, diagnostic_notes: e.target.value })}
                  placeholder="Descrieti constatarile si observatiile..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Tasks */}
        <TabsContent value="tasks">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Planned Tasks */}
            <Card className="card-industrial">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" />
                    Sarcini Planificate
                  </CardTitle>
                  <Button size="sm" onClick={() => { setNewTask({ description: '', is_planned: true }); setShowAddTaskDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Adauga
                  </Button>
                </div>
                <CardDescription>Sarcini definite in procedura de lucru</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tasksPlanned.length > 0 ? tasksPlanned.map((task) => (
                    <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg border ${task.completed ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                      <Checkbox 
                        checked={task.completed}
                        onCheckedChange={() => handleToggleTaskComplete(task.id, true)}
                      />
                      <span className={`flex-1 ${task.completed ? 'line-through text-slate-400' : ''}`}>
                        {task.description}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id, true)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nu exista sarcini planificate</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Executed Tasks */}
            <Card className="card-industrial">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Sarcini Executate
                  </CardTitle>
                  <Button size="sm" onClick={() => { setNewTask({ description: '', is_planned: false }); setShowAddTaskDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Adauga
                  </Button>
                </div>
                <CardDescription>Sarcini adaugate in timpul executiei</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tasksExecuted.length > 0 ? tasksExecuted.map((task) => (
                    <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg border ${task.completed ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                      <Checkbox 
                        checked={task.completed}
                        onCheckedChange={() => handleToggleTaskComplete(task.id, false)}
                      />
                      <span className={`flex-1 ${task.completed ? 'line-through text-slate-400' : ''}`}>
                        {task.description}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id, false)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nu exista sarcini adaugate in executie</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Consumables */}
        <TabsContent value="consumables">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Planned Consumables */}
            <Card className="card-industrial">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Consumabile Planificate
                  </CardTitle>
                  <Button size="sm" onClick={() => setShowAddConsumableDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Adauga
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {consumablesPlanned.length > 0 ? consumablesPlanned.map((cons, idx) => (
                    <div key={cons.id || idx} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                      <div>
                        <p className="font-medium">{cons.article_name}</p>
                        <p className="text-sm text-slate-500">{cons.article_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{cons.quantity_planned} {cons.unit}</p>
                        <p className="text-sm text-slate-500">{(cons.quantity_planned * (cons.unit_price || 0)).toFixed(2)} LEI</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nu exista consumabile planificate</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Used Consumables */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Consumabile Utilizate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {consumablesUsed.length > 0 ? consumablesUsed.map((cons, idx) => (
                    <div key={cons.id || idx} className="flex items-center justify-between p-3 rounded-lg border bg-green-50 border-green-200">
                      <div>
                        <p className="font-medium">{cons.article_name}</p>
                        <p className="text-sm text-slate-500">Prelevat din: {cons.bin_code || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{cons.quantity_used} {cons.unit}</p>
                        <p className="text-sm text-slate-500">{(cons.quantity_used * (cons.unit_price || 0)).toFixed(2)} LEI</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nu exista consumabile utilizate</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Documents */}
        <TabsContent value="documents">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documente Tehnice
              </CardTitle>
              <CardDescription>Incarcati documente relevante pentru interventie</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#0052CC] transition-colors mb-4">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
                  onChange={(e) => handleFileUpload(e, 'document')}
                  className="hidden"
                  id="document-upload"
                />
                <label htmlFor="document-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Click pentru a incarca document</p>
                  <p className="text-xs text-slate-400">PDF, DOC, XLS, JPG, PNG</p>
                </label>
              </div>
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleString('ro-RO')}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setDocuments(documents.filter(d => d.id !== doc.id))}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Videos */}
        <TabsContent value="videos">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Video className="w-5 h-5" />
                Video Interventie
              </CardTitle>
              <CardDescription>Incarcati inregistrari video (max 32MB)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#0052CC] transition-colors mb-4">
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={(e) => handleFileUpload(e, 'video')}
                  className="hidden"
                  id="video-upload"
                />
                <label htmlFor="video-upload" className="cursor-pointer">
                  <Video className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Click pentru a incarca video</p>
                  <p className="text-xs text-slate-400">MP4, WEBM (max 32MB)</p>
                </label>
              </div>
              <div className="space-y-2">
                {videos.map((vid) => (
                  <div key={vid.id} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium">{vid.name}</p>
                        <p className="text-xs text-slate-500">{new Date(vid.uploaded_at).toLocaleString('ro-RO')}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setVideos(videos.filter(v => v.id !== vid.id))}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Costs */}
        <TabsContent value="costs">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Costuri Interventie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Planned Costs */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Costuri Planificate</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Ore manopera planificate</Label>
                      <Input
                        type="number"
                        value={costs.labor_hours_planned}
                        onChange={(e) => setCosts({ ...costs, labor_hours_planned: parseFloat(e.target.value) || 0 })}
                        className="w-24 h-10 text-right"
                        step="0.5"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Cost ora (LEI)</Label>
                      <Input
                        type="number"
                        value={costs.labor_hourly_rate}
                        onChange={(e) => setCosts({ ...costs, labor_hourly_rate: parseFloat(e.target.value) || 0 })}
                        className="w-24 h-10 text-right"
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Cost manopera:</span>
                      <span className="font-medium">{costs.labor_cost_planned.toFixed(2)} LEI</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Cost materiale:</span>
                      <span className="font-medium">{costs.materials_cost_planned.toFixed(2)} LEI</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between text-lg">
                      <span className="font-semibold">TOTAL PLANIFICAT:</span>
                      <span className="font-bold text-[#0052CC]">{costs.total_cost_planned.toFixed(2)} LEI</span>
                    </div>
                  </div>
                </div>

                {/* Actual Costs */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Costuri Efective</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Ore manopera efective</Label>
                      <Input
                        type="number"
                        value={costs.labor_hours_actual}
                        onChange={(e) => setCosts({ ...costs, labor_hours_actual: parseFloat(e.target.value) || 0 })}
                        className="w-24 h-10 text-right"
                        step="0.5"
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Cost manopera:</span>
                      <span className="font-medium">{costs.labor_cost_actual.toFixed(2)} LEI</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Cost materiale:</span>
                      <span className="font-medium">{costs.materials_cost_actual.toFixed(2)} LEI</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between text-lg">
                      <span className="font-semibold">TOTAL EFECTIV:</span>
                      <span className="font-bold text-green-600">{costs.total_cost_actual.toFixed(2)} LEI</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Task Dialog */}
      <Dialog open={showAddTaskDialog} onOpenChange={setShowAddTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adauga Sarcina {newTask.is_planned ? 'Planificata' : 'Executata'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Descriere sarcina *</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Descrieti sarcina..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTaskDialog(false)}>Anuleaza</Button>
            <Button onClick={handleAddTask}>Adauga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Consumable Dialog */}
      <Dialog open={showAddConsumableDialog} onOpenChange={setShowAddConsumableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adauga Consumabil</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Articol *</Label>
              <ArticleAutocomplete
                articles={consumables}
                onSelect={(article) => setNewConsumable({ ...newConsumable, article_id: article.id })}
                placeholder="Cauta articol dupa cod sau denumire..."
              />
            </div>
            <div className="space-y-2">
              <Label>Cantitate planificata</Label>
              <Input
                type="number"
                value={newConsumable.quantity_planned}
                onChange={(e) => setNewConsumable({ ...newConsumable, quantity_planned: parseFloat(e.target.value) || 0 })}
                className="h-11"
                min="0"
                step="1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddConsumableDialog(false)}>Anuleaza</Button>
            <Button onClick={handleAddConsumable}>Adauga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevisionDetailPage;
